import { bake_cookie, delete_cookie, read_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from "../../components/utils/url-manager";
import { AclPermissions, permissionManager } from '../../components/permissions/manager';

/**
 * Which ACL resource each registered react-admin resource and action needs.
 *
 * **This map is most of HQ's permission enforcement, and almost none of it is written by hand.**
 * `EditButton`, `ShowButton`, `CreateButton`, `ListButton` and `DeleteButton` all ask
 * `useCanAccess` themselves and render nothing when refused, and the list controller calls
 * `useRequireAccess` on the route. So naming the verb for each action here gates every standard
 * control on all 56 resources without a single component knowing about it.
 *
 * That is also why an explicit `isAllowed` check wrapped around one of those buttons should be
 * deleted rather than updated: it is the same rule written twice, and two copies of a permission
 * rule drift. Add a check only where react-admin has no way to ask - a custom button, a whole tab,
 * a value passed as a prop, a dashboard card that mounts a `<List>`.
 *
 * **A resource missing from the map answers true.** react-admin treats an absent `canAccess` as
 * "allow everything", and a map that denied by default would empty HQ.
 *
 * A verb the tree does not declare denies, which is the useful direction and is deliberate:
 * `nok_processing_failures` declares `view` alone, so the create, edit and delete entries below
 * name resources that do not exist, the server never lists them as allowed, and those actions are
 * refused. Writing them out rather than omitting them is what makes that a decision rather than an
 * oversight, since omitting them would allow them.
 */
const crud = (section: string): Record<string, string | string[]> => ({
    list: section + '.view',
    show: section + '.view',
    create: section + '.create',
    edit: section + '.edit',
    delete: section + '.delete',
});

/** A resource that is only ever read, so the write actions are simply absent from the API. */
const readOnly = (view: string | string[]): Record<string, string | string[]> => ({
    list: view,
    show: view,
});

const ACL_RESOURCES: Record<string, Record<string, string | string[]>> = {
    // Creating and deleting an admin used to be a sibling resource, `HQ_Admin::admin_users_manage`.
    // The verbs say it now, and `acl:migrate:verbs` rewrote the roles that held the old pair.
    admin_users: crud('HQ_Admin::admin_users'),
    admin_roles: crud('HQ_Admin::roles'),
    settings: crud('HQ_Admin::settings'),
    llm_model_prices: crud('HQ_Admin::llm_prices'),

    customers: crud('HQ_Customer::customers'),
    customer_accounts: crud('HQ_Customer::accounts'),
    // Its own section, not the customer one. This said `HQ_Customer::customers` until per-verb
    // permissions landed, which made the browser narrower than the API: `CustomerAddress` has
    // always been gated on `HQ_Customer::customer_addresses`, a deliberate sibling of the customer
    // record because an address sat at the admin tier and the record above it. A role holding the
    // addresses and not the records could reach them through the API and not through HQ.
    customer_addresses: crud('HQ_Customer::customer_addresses'),
    customer_tutorials: crud('HQ_Customer::tutorials'),
    // Append-only: `CustomerActivityMonitor` declares Get and GetCollection and nothing else.
    customer_activity_monitors: readOnly('HQ_Customer::customers.view'),

    cms_blocks: crud('HQ_Content::cms_blocks'),

    // Companies and contacts sit in one sidebar group and two different tiers. See config/acl.php.
    companies: crud('HQ_Data::companies'),
    addresses: crud('HQ_Data::companies'),
    emails: crud('HQ_Data::companies'),
    employees: crud('HQ_Data::companies'),
    telephones: crud('HQ_Data::companies'),
    websites: crud('HQ_Data::companies'),
    urls: crud('HQ_Data::companies'),
    contacts: crud('HQ_Data::crm'),
    social_media: crud('HQ_Data::crm'),

    companies_houses: crud('HQ_Data::companies_house'),
    companies_house_addresses: crud('HQ_Data::companies_house'),
    companies_house_peoples: crud('HQ_Data::companies_house'),
    filing_histories: crud('HQ_Data::companies_house'),

    // Written by whoever holds reference data, and read by anyone holding either that or contacts,
    // because the Nok forms look countries and industries up at the contacts tier. Two ids because
    // the expression on the entity names two; one would make the browser disagree with the API.
    countries: {
        ...crud('HQ_Data::reference_data'),
        list: ['HQ_Data::reference_data.view', 'HQ_Data::crm.view'],
        show: ['HQ_Data::reference_data.view', 'HQ_Data::crm.view'],
    },
    industries: {
        ...crud('HQ_Data::reference_data'),
        list: ['HQ_Data::reference_data.view', 'HQ_Data::crm.view'],
        show: ['HQ_Data::reference_data.view', 'HQ_Data::crm.view'],
    },

    sales_conversations: crud('HQ_Sales::conversation'),
    enterprise_requests: crud('HQ_Sales::enterprise_requests'),
    contact_forms: crud('HQ_Sales::contact_forms'),
    coupons: crud('HQ_Sales::coupons'),
    subscriptions: crud('HQ_Sales::subscriptions'),
    subscription_invoices: crud('HQ_Sales::invoices'),
    subscription_invoice_items: crud('HQ_Sales::invoices'),
    subscription_products: crud('HQ_Sales::products'),
    subscription_product_prices: crud('HQ_Sales::product_prices'),
    subscription_refunds: crud('HQ_Sales::refunds'),

    noks: crud('HQ_Nok::noks'),
    nok_minis: crud('HQ_Nok::minis'),
    mini_grades: crud('HQ_Nok::mini_grades'),
    mini_results: crud('HQ_Nok::mini_results'),
    nok_personas: crud('HQ_Nok::faux_nokkers'),
    nok_websites: crud('HQ_Nok::faux_nokkers'),
    nok_email_templates: crud('HQ_Nok::faux_nokkers'),
    nok_email_signatures: crud('HQ_Nok::faux_nokkers'),

    email_finders: crud('HQ_Data::email_finder'),
    email_finder_results: crud('HQ_Data::email_finder_results'),
    mystery_shoppers: crud('HQ_Data::mystery_shopper'),
    mystery_shopper_results: crud('HQ_Data::mystery_shopper_results'),
    compliance_sniffers: crud('HQ_Data::compliance_sniffer'),
    compliance_sniffer_results: crud('HQ_Data::compliance_sniffer_results'),
    // The pipeline writes these and an admin reads them, so the section declares `view` alone and
    // the other three entries above deny by naming resources the tree does not have.
    nok_processing_failures: crud('HQ_Data::nok_processing_failures'),

    // Deliberately absent: `email_messages` and `email_metrics`. Both are registered resources
    // whose entities now name HQ_Nok::noks, but the sales conversations screen reads email
    // messages too, so mapping it here would have `canAccess` deny a sales-only admin a thread
    // they are entitled to. Absent means allowed, and HQ-API is still the gate: `EmailMessage`
    // picks its resource off the record's channel and `EmailMessageChannelExtension` filters the
    // collection. See HQAPI-80.

    operators: crud('HQ_System::operators'),
    token_histories: crud('HQ_System::token_histories'),
    tools: crud('HQ_System::tools'),
    workflows: crud('HQ_System::workflows'),
    prompts: crud('HQ_System::prompts'),
    model_schemas: crud('HQ_System::model_schemas'),
};

/**
 * The id in an `#/admin_users/<id>` route, or null anywhere else.
 *
 * A TabbedForm puts the tab index in the route as well, so `#/admin_users/17/2` is the third tab
 * of admin 17; stopping at the next slash is what keeps both shapes working. `create` matches the
 * pattern and simply never equals an id.
 */
const adminUserRouteId = (): string | null => {
    const match: RegExpMatchArray | null = window.location.hash.match(/^#\/admin_users\/([^/?]+)/);

    return match === null ? null : decodeURIComponent(match[1]);
};

/**
 * Whether the route being asked about is the signed-in admin's own record.
 *
 * **This is the browser's copy of `object == user`**, which `AdminUser` carries on Get, Put and
 * Patch so that every admin can read and edit their own record whatever their role. That is what
 * keeps the self-service password change working - `AdminUserEdit` shows the Password tab on
 * `id === record.id` - and without the same answer here the tab is unreachable: the route is
 * refused before the form mounts, and the admin lands on `/access-denied`. Measured 2026-09-10
 * (HQ-27): the API let a narrow role change its own password while HQ would not open the screen.
 *
 * **It reads the route rather than the record, because there is no record to read.**
 * `useEditController` calls `useRequireAccess({ action: 'edit', resource })` and passes no record
 * at all, so a record-aware check would never fire on the route that matters. The id in the URL is
 * what exists at that moment.
 *
 * Answers false on anything unexpected, which leaves the ACL map as the only opinion.
 */
const isOwnAdminRecord = (): boolean => {
    const routeId: string | null = adminUserRouteId();
    if (routeId === null) {
        return false;
    }

    try {
        const stored: string | null = localStorage.getItem('profile');
        const profile: any = stored === null ? null : JSON.parse(stored);

        return profile != null && String(profile.id) === routeId;
    } catch (error) {
        return false;
    }
};

type TokenCheck = {
    /** The status HQ-API answered with, or null when the call could not be made at all. */
    status: number|null,
    /** The permissions payload, when the call succeeded. */
    permissions: AclPermissions,
}

/**
 * What an admin who could not be asked about is allowed: nothing, in the right shape.
 *
 * Every failure path used to answer `[]` while the success path answered a string, so
 * `permissions` was `string | any[]` and nothing validated it. Answering one shape throughout
 * means `permissionManager` never has to guess, and an unanswered call reads as "no permissions"
 * rather than as a different kind of value.
 */
const NO_PERMISSIONS: AclPermissions = { roles: '', role: null, allowed: [] };

/**
 * Read the `auth` cookie as a token.
 *
 * sfcookies answers a missing cookie with `[]` rather than undefined, so the type check is
 * what separates "no cookie" from "a token".
 */
function readAuthToken(): string {
    const cookieVal: any = read_cookie('auth');

    return typeof cookieVal === 'string' ? cookieVal : '';
}

/**
 * Ask HQ-API whether the token is still good, and what it is allowed to do.
 *
 * `/hq/user/permissions` sits behind AdminUserAuthenticator, so it answers 401 for a token
 * that has expired, been revoked or been deleted, and 200 with the caller's roles otherwise.
 * That makes it the cheapest thing to ask "am I still logged in".
 */
async function requestPermissions(token: string): Promise<TokenCheck> {
    const url: string = UseUrlManagerCleanUrl( { url: hostUrl + 'hq/user/permissions' } );
    // react-admin waits on `checkAuth` before it renders a route, so an HQ-API that hangs would
    // leave the panel showing nothing indefinitely. Give up after a few seconds and answer
    // "unknown", which leaves the admin signed in rather than stranded.
    const controller: AbortController = new AbortController();
    const timeout: any = setTimeout( (): void => controller.abort(), TOKEN_CHECK_TIMEOUT_MS );
    const request: Request = new Request(url, {
        method: 'GET',
        signal: controller.signal,
        headers: new Headers({
            'Content-Type': 'application/json',
            'token': token,
            'hq': 'hq'
        })
    });

    try {
        const response: Response = await fetch(request);
        if (response.status === 200) {
            const body: any = await response.json();

            // An HQ-API from before 2026-09-10 answers a bare roles string here. Normalising it
            // rather than rejecting it keeps a tab open across a deploy working, with the ROLE_
            // half live and the ACL half empty.
            return {
                status: 200,
                permissions: typeof body === 'string'
                    ? { roles: body, role: null, allowed: [] }
                    : {
                        roles: typeof body?.roles === 'string' ? body.roles : '',
                        role: typeof body?.role === 'string' ? body.role : null,
                        allowed: Array.isArray(body?.allowed) ? body.allowed : [],
                    },
            };
        }

        return { status: response.status, permissions: NO_PERMISSIONS };
    } catch (error) {
        // The call could not be made, or it timed out. That says nothing about the token.
        console.log(error);

        return { status: null, permissions: NO_PERMISSIONS };
    } finally {
        clearTimeout(timeout);
    }
}

/** How long to wait on HQ-API before giving up and answering "unknown". */
const TOKEN_CHECK_TIMEOUT_MS: number = 8000;

/**
 * How long a verdict on a token is reused for.
 *
 * react-admin awaits `checkAuth` before rendering a route, so asking HQ-API on every navigation
 * puts a round trip in front of every page. That is invisible against a fast API and very
 * noticeable against a slow one. A token that was live a moment ago is still live, so the
 * answer is held briefly and most navigations resolve with no request at all.
 *
 * The cost is that a token revoked mid-session is noticed up to this long afterwards, which is
 * the ordinary trade for a session check and still far better than the never it was before.
 */
const TOKEN_CHECK_TTL_MS: number = 60000;

type CachedCheck = {
    token: string,
    checkedAt: number,
    result: TokenCheck,
}

let cachedCheck: CachedCheck|null = null;
let pendingCheck: Promise<TokenCheck>|null = null;
let pendingToken: string = '';

/** Drop any held verdict, so the next check asks HQ-API again. */
function clearTokenCheck(): void {
    cachedCheck = null;
    pendingCheck = null;
    pendingToken = '';
}

/**
 * The current verdict on a token, from cache when it is recent enough.
 *
 * Concurrent callers share one request: react-admin fires `checkAuth` and `getPermissions`
 * together on every navigation and both want the same answer.
 */
function verifyToken(token: string): Promise<TokenCheck> {
    if (
        cachedCheck !== null
        && cachedCheck.token === token
        && (Date.now() - cachedCheck.checkedAt) < TOKEN_CHECK_TTL_MS
    ) {
        return Promise.resolve(cachedCheck.result);
    }

    if (pendingCheck === null || pendingToken !== token) {
        pendingToken = token;
        pendingCheck = requestPermissions(token).then( (result: TokenCheck): TokenCheck => {
            // Only a definite answer is worth holding on to. A call that failed or timed out
            // says nothing about the token, so caching it would suppress the next real check.
            if (result.status !== null) {
                cachedCheck = { token: token, checkedAt: Date.now(), result: result };
            }
            pendingCheck = null;

            return result;
        } );
    }

    return pendingCheck;
}

/** Shown when HQ-API refuses the credentials and says nothing more useful itself. */
const INVALID_CREDENTIALS_MESSAGE: string = 'Invalid email or password';

/** Shown when the login request could not be made at all. */
const UNREACHABLE_MESSAGE: string = 'Could not reach the server, please try again';

/**
 * Read a response as JSON, or null when it is not JSON at all.
 *
 * A proxy error page, a PHP fatal or an empty body all make `response.json()` throw, and its
 * "Unexpected token '<'" is not something to put in front of an admin.
 */
async function readJson(response: Response): Promise<any> {
    try {
        return await response.json();
    } catch {
        return null;
    }
}

/**
 * The reason HQ-API gave for refusing a request, when it gave one.
 *
 * The trailing full stop is dropped on purpose: react-admin puts a notification through
 * polyglot, which reads a dot as a key separator, so "Invalid credentials." can translate to
 * nothing where "Invalid credentials" comes through unchanged.
 */
async function readApiError(response: Response): Promise<string|null> {
    const body: any = await readJson(response);
    const message: string = String(body?.error ?? body?.message ?? '').replace(/\.+$/, '').trim();

    return message.length > 0 ? message : null;
}

const authProvider = {
    login: async ({username, password}: { username: string, password: string }): Promise<void> => {
        const url: string = UseUrlManagerCleanUrl({ url: hostUrl + 'admin/user/login' });
        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify({username, password}),
            headers: new Headers({
                'Content-Type': 'application/json',
            })
        });

        let response: Response;
        try {
            response = await fetch(request);
        } catch (error) {
            // The request never reached HQ-API, so nothing is known about the credentials.
            console.log(error);
            throw new Error(UNREACHABLE_MESSAGE);
        }

        if (response.status < 200 || response.status >= 300) {
            // This used to report `response.statusText`, which is the empty string on every
            // HTTP/2 connection, and `??` does not fall back for an empty string. That is why
            // a refused login raised a notification with no text in it. HQ-API answers a bad
            // password with `{"error": "Invalid credentials."}`, which is worth showing;
            // anything unreadable falls back to the wording below.
            throw new Error(await readApiError(response) ?? INVALID_CREDENTIALS_MESSAGE);
        }

        const auth: any = await readJson(response);
        if (typeof auth?.token !== 'string' || auth.token.length === 0) {
            // A 2xx with no token in it is not a login, whatever the status said.
            throw new Error(INVALID_CREDENTIALS_MESSAGE);
        }

        // A verdict held against the previous token must not answer for this one.
        clearTokenCheck();
        bake_cookie('auth', auth.token);
        localStorage.setItem('profile', JSON.stringify({
            id: auth.id,
            fullName: auth.name,
            avatar: hostUrl + auth.avatar
        }));
    },
    logout: async (): Promise<void> => {
        //call the endpoint to trigger the logout actions
        const url: string = hostUrl + 'admin/user/logout';
        const logoutRequest = new Request(url, {
            method: 'POST',
            body: null,
            headers: new Headers({
                'Content-Type': 'application/json'
            })
        });
        try {
            const response: Response = await fetch(logoutRequest);
            if (response.status < 200 || response.status >= 300) {
                console.log(response.statusText);
            }
            delete_cookie('auth');
            localStorage.removeItem('profile');
            clearTokenCheck();
        } catch (error) {
            console.log(error);
        }
        return Promise.resolve();

    },
    checkAuth: async (): Promise<void> => {
        const token: string = readAuthToken();
        if (token.length === 0) {
            // `message: false` suppresses the notification. Rejecting with the bare
            // string 'login' made react-admin show it as an error toast, so every
            // logged-out visit to the login page raised a red "login" snackbar.
            return Promise.reject({ message: false })
        }

        const check: TokenCheck = await verifyToken(token);
        if (check.status === 401 || check.status === 403) {
            // The cookie is still in the browser but HQ-API will not accept the token in it
            // any more, because it has expired, been revoked or been deleted. Checking only
            // that the cookie existed left the admin inside the panel with every list coming
            // back empty and nothing ever signing them out. Rejecting here makes react-admin
            // run `logout()` and return them to the login page.
            return Promise.reject({ message: false })
        }

        // Any other answer, a 500 or a call that could not be made, is not evidence the token
        // is dead. Leave them signed in; `checkError` catches it if a real request refuses.
        return Promise.resolve();
    },
    checkError:  (error: any): Promise<void> => {
        const status: number|undefined = error?.status ?? error?.response?.status;
        if (status === 401) {
            // react-admin signs the admin out and returns them to the login page when this
            // rejects. A 401 is the token being dead, which is exactly that.
            return Promise.reject({ message: false });
        }

        // **A 403 must not sign anyone out.** It used to, alongside the 401, and that was
        // defensible while every refusal meant a stale role. It stopped being defensible on
        // 2026-09-10: the ACL exists so that some requests are refused, and an admin whose role
        // is deliberately narrow was ejected to the login screen by their own dashboard, which
        // loads panels for resources they cannot reach. They could not stay signed in at all.
        //
        // A 403 says the token is good and the caller may not do this one thing. The screen that
        // asked shows the refusal; the session is not in question.
        //
        // A 500, a timeout or a network failure is not an authentication problem either, and
        // signing someone out over one would throw away whatever they were editing.
        return Promise.resolve();
    },
    getIdentity: (): Promise<{ id: number, fullName: string, avatar: string }> => {
        try {
            const profile: any = JSON.parse(localStorage.getItem('profile'));
            if (profile) {
                const { id, fullName, avatar } = profile;
                return Promise.resolve({ id, fullName, avatar });
            } else {
                return Promise.reject({ message: false })
            }
        }  catch (error) {
            return Promise.reject({ message: false })
        }
    },
    /**
     * react-admin's own access control. `EditButton`, `CreateButton`, `ListButton` and
     * `ResourceMenuItem` all call this through `useCanAccess`, so defining it gates every standard
     * control without touching a component.
     *
     * **A resource missing from the map answers true.** react-admin treats an absent `canAccess`
     * as "allow everything", so the moment this exists every built-in button starts asking; a map
     * that denied by default would empty HQ. The map is therefore the migration checklist: a
     * resource enters it when its area has moved to the ACL, and only then does it start
     * refusing.
     */
    canAccess: async ({ resource, action }: { resource: string, action: string }): Promise<boolean> => {
        // Any admin may reach their own record, which is the browser's half of `object == user` on
        // the entity and the only route to the Password tab. See `isOwnAdminRecord`.
        if (resource === 'admin_users' && (action === 'edit' || action === 'show') && isOwnAdminRecord()) {
            return true;
        }

        const required: string | string[] | undefined = ACL_RESOURCES[resource]?.[action]
            ?? ACL_RESOURCES[resource]?.['*'];
        if (required === undefined) {
            return true;
        }

        // A list rather than a single id where the entity's own expression names two, which
        // countries and industries do. Answering on one of them would make the browser refuse a
        // screen HQ-API serves, and a gate that is narrower than the API is still a bug.
        const permissions: AclPermissions = await authProvider.getPermissions();
        const wanted: string[] = Array.isArray(required) ? required : [required];

        return wanted.some((id: string): boolean => permissionManager.isAllowed(permissions, id));
    },
    getPermissions: async (): Promise<AclPermissions> => {
        const token: string = readAuthToken();

        // No token means we are on the login page. Asking the API for permissions
        // here only ever produces a 401 ("No Valid Token Found"), which surfaced as
        // an error toast over the login form.
        if (token.length === 0) {
            return NO_PERMISSIONS;
        }

        // Treat anything other than a clean 200 as "no permissions" rather than rejecting.
        // Rejecting here does not sign anyone out anyway, since react-admin only surfaces it
        // as an error on `usePermissions`; `checkAuth` is what acts on a dead token.
        return (await verifyToken(token)).permissions;
    },
};

export default authProvider;
